const PRODUCTION_APP_HOST = 'conference.onkospace.de'
const PRODUCTION_API_ORIGIN = 'https://conference-api.onkospace.de'

function withoutTrailingSlash (value) {
  return String(value || '').replace(/\/+$/, '')
}

function configuredServiceUrl (value, runtimeOrigin, defaultPath = '') {
  const configured = String(value || '').trim()
  if (!configured) return null
  try {
    const url = new URL(configured, runtimeOrigin)
    if (defaultPath && (!url.pathname || url.pathname === '/')) {
      url.pathname = defaultPath
    }
    return withoutTrailingSlash(url.toString())
  } catch {
    return null
  }
}

export function resolveServiceUrls (locationLike, env = {}) {
  const hostname = String(locationLike?.hostname || '').toLowerCase()
  const runtimeOrigin = withoutTrailingSlash(
    locationLike?.origin || `${locationLike?.protocol || 'https:'}//${hostname || PRODUCTION_APP_HOST}`
  )
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || hostname === '[::1]'
  const localHost = hostname.includes(':') && !hostname.startsWith('[') ? `[${hostname}]` : hostname
  const defaultApiOrigin = isLocal
    ? `http://${localHost || 'localhost'}:5001`
    : hostname === PRODUCTION_APP_HOST
      ? PRODUCTION_API_ORIGIN
      : runtimeOrigin

  return {
    conferenceApiUrl: configuredServiceUrl(
      env.REACT_APP_CONFERENCE_API_URL,
      runtimeOrigin,
      '/api/conference'
    ) || `${defaultApiOrigin}/api/conference`,
    authApiUrl: configuredServiceUrl(
      env.REACT_APP_AUTH_API_URL,
      runtimeOrigin,
      '/api/auth'
    ) || `${defaultApiOrigin}/api/auth`,
    socketUrl: configuredServiceUrl(
      env.REACT_APP_SOCKET_URL,
      runtimeOrigin
    ) || defaultApiOrigin,
  }
}

const runtimeLocation = typeof window !== 'undefined'
  ? window.location
  : { hostname: PRODUCTION_APP_HOST, origin: `https://${PRODUCTION_APP_HOST}`, protocol: 'https:' }
const serviceUrls = resolveServiceUrls(runtimeLocation, process.env)
const BASE_URL = serviceUrls.conferenceApiUrl
const AUTH_URL = serviceUrls.authApiUrl
const SOCKET_URL = serviceUrls.socketUrl

const CONF_TOKEN_KEY = 'conf_auth_token'

function expireLegacyCookie (name, path) {
  if (typeof document === 'undefined') return
  const encodedName = encodeURIComponent(name)
  const pathAttribute = path ? `; Path=${path}` : ''
  document.cookie = `${encodedName}=; Max-Age=0${pathAttribute}; SameSite=Lax`
}

export function clearLegacyAuthCookies () {
  for (const name of [CONF_TOKEN_KEY, 'auth_token']) {
    // Legacy builds used both the current document path and `/`. Expire both
    // variants without accepting a caller-controlled attributes object.
    expireLegacyCookie(name, '/')
    expireLegacyCookie(name)
  }
}

// ── Per-conference participant token (HMAC) ───────────────────────────────
// Server returns this on POST /:id/join. We store it in localStorage scoped
// by conference id, then auto-attach it as `X-Participant-Token` for every
// state-changing request that targets `/{conferenceId}/...`. This prevents
// participant impersonation: even if an attacker reads someone else's
// participant_id from /state, they can't act as them without the token.
const PT_PREFIX = 'cc_pt_'
const participantTokenMemory = new Map()
export function setParticipantToken (conferenceId, token) {
  if (!conferenceId || !token) return
  // Keep the current tab functional when storage is blocked (strict privacy
  // mode, sandboxed iframe, quota/security errors). Persistence becomes
  // best-effort, but HTTP + Socket.IO can still authenticate this live visit.
  participantTokenMemory.set(conferenceId, token)
  try { localStorage.setItem(PT_PREFIX + conferenceId, token) } catch {}
  // Socket.IO may already be connected when an expired participant token is
  // refreshed in-place. Tell SocketContext to re-authenticate the room now;
  // waiting for the next socket reconnect would leave live data notifications
  // missing even though HTTP state had recovered.
  try {
    window.dispatchEvent(new CustomEvent('cc:participant-token-changed', {
      detail: { conferenceId },
    }))
  } catch {}
}
export function getParticipantToken (conferenceId) {
  if (participantTokenMemory.has(conferenceId)) return participantTokenMemory.get(conferenceId)
  try { return localStorage.getItem(PT_PREFIX + conferenceId) } catch { return null }
}
export function clearParticipantToken (conferenceId) {
  participantTokenMemory.delete(conferenceId)
  try { localStorage.removeItem(PT_PREFIX + conferenceId) } catch {}
  // Force an already-connected participant socket to re-authenticate. The
  // server drops its previous room before rejecting the now-incomplete pair,
  // so invalidated credentials cannot retain a stale subscription.
  try {
    window.dispatchEvent(new CustomEvent('cc:participant-token-changed', {
      detail: { conferenceId, present: false, reason: 'invalid' },
    }))
  } catch {}
}

// ── Per-conference resume secret ──────────────────────────────────────────
// High-entropy value the server issues once, on the first join that claims a
// participant row. Stored OUTSIDE the URL (localStorage only) and replayed on
// reconnect so a returning browser can re-bind to its row even after the HMAC
// participant token is lost — without letting anyone who merely knows the
// URL's user_identifier take the row over. Never goes in a link or the URL bar.
const RS_PREFIX = 'cc_rs_'
const resumeSecretMemory = new Map()
export function setResumeSecret (conferenceId, secret) {
  if (!conferenceId || !secret) return
  resumeSecretMemory.set(conferenceId, secret)
  try { localStorage.setItem(RS_PREFIX + conferenceId, secret) } catch {}
}
export function getResumeSecret (conferenceId) {
  if (resumeSecretMemory.has(conferenceId)) return resumeSecretMemory.get(conferenceId)
  try { return localStorage.getItem(RS_PREFIX + conferenceId) } catch { return null }
}
export function clearResumeSecret (conferenceId) {
  resumeSecretMemory.delete(conferenceId)
  try { localStorage.removeItem(RS_PREFIX + conferenceId) } catch {}
}

// Convenience: read the local participant id stored by ConferenceRoom on
// join. Used by SocketContext to authenticate `join-room` so the server
// can refuse eavesdroppers.
export function getParticipantId (conferenceId) {
  if (!conferenceId) return null
  try {
    const raw = localStorage.getItem(`cc_participant_${conferenceId}`)
    if (!raw) return null
    const data = JSON.parse(raw)
    return data?.participantId || null
  } catch { return null }
}

// Extract the leading conference id from paths like "/{uuid}/vote/cast" or "/{uuid}".
const CONF_ID_RE = /^\/([0-9a-f-]{36})(?:\/|$)/i
function conferenceIdFromPath (path) {
  const m = CONF_ID_RE.exec(path || '')
  return m ? m[1] : null
}

// Convenience accessor — used by route guards to decide if user is logged in
// as admin. Returns the JWT cookie value or null.
function getHeaders (path, { json = true } = {}) {
  // Content-Type only with a body: on a GET it is pointless and turns a plain
  // lobby poll into a CORS-preflighted request.
  const headers = json ? { 'Content-Type': 'application/json' } : {}
  // The admin JWT is NOT attached here anymore — it rides in the HttpOnly
  // session cookie and is sent automatically via credentials:'include'. Page
  // JS can no longer read it (CC-16). Only the participant token (a non-admin
  // HMAC kept in localStorage) is attached, for /{uuid}/ paths.
  const confId = conferenceIdFromPath(path)
  if (confId) {
    const pt = getParticipantToken(confId)
    if (pt) headers['X-Participant-Token'] = pt
  }
  return headers
}

// Shared non-2xx handler for ALL verbs (IMP-1 / AUTH-3). Previously only
// apiPost recovered from an expired admin session; apiGet/apiPut/apiDelete just
// threw "HTTP 401", so e.g. the Import modal (which loads its list via apiGet)
// would hang forever on a stale session with no re-login path. This always
// throws — callers can assume a resolved fetch past it was 2xx.
async function throwForStatus (res, path, requestParticipantToken) {
  const data = await res.json().catch(error => {
    // Reading the response body is still part of the request timeout. Do not
    // turn a cancelled/stalled body into a misleading HTTP error.
    if (error?.name === 'AbortError') throw error
    return {}
  })
  // Participant token errors self-heal in-place through the state hook's
  // resume-secret /join recovery (and on next mount as a fallback).
  if (res.status === 401 && (data.code === 'PARTICIPANT_TOKEN_INVALID' || /participant token/i.test(data.error || ''))) {
    const confId = conferenceIdFromPath(path)
    // A request sent before an in-place token refresh can finish afterwards.
    // Its rejected old token must not erase the newly recovered credentials.
    if (confId && getParticipantToken(confId) === requestParticipantToken) clearParticipantToken(confId)
  } else if (res.status === 401 && /invalid|expired|account not found|authentication/i.test(data.error || '')) {
    // Admin session lost/expired. The JWT lives in an HttpOnly cookie we can't
    // clear from JS, so ask the server to clear it, then route to the landing
    // page for re-login. Throw a friendly message so the toast is actionable.
    fetch(`${AUTH_URL}/logout`, { method: 'POST', credentials: 'include' }).catch(() => {})
    if (typeof window !== 'undefined' && !window.location.pathname.endsWith('/')) {
      setTimeout(() => { window.location.href = '/' }, 1200)
    }
    const error = new Error('Sitzung abgelaufen. Bitte melden Sie sich erneut an.')
    error.status = 401
    error.code = 'AUTHENTICATION_REQUIRED'
    throw error
  }
  // Surface the server's German body (incl. 429 "Zu viele Anfragen…") instead
  // of a bare "HTTP 429". Attach status + retry_after_ms so callers can retry.
  const err = new Error(data.error || `HTTP ${res.status}`)
  err.status = res.status
  if (data.retry_after_ms != null) err.retryAfterMs = data.retry_after_ms
  // Machine-readable error code + full body (e.g. already_claimed lets the
  // join screen explain resume-secret/admin-reset recovery).
  if (data.code) err.code = data.code
  err.data = data
  throw err
}

const DEFAULT_API_TIMEOUT_MS = 15000
const DEFAULT_AUTH_TIMEOUT_MS = 10000

async function conferenceRequest (
  path,
  {
    method = 'GET',
    body,
    timeoutMs = DEFAULT_API_TIMEOUT_MS,
    signal: externalSignal,
  } = {}
) {
  // A half-open mobile/Wi-Fi connection can leave fetch pending for minutes.
  // useConferenceState serialises requests, so one hung fetch otherwise blocks
  // every socket ping, heartbeat refresh, and online recovery behind it.
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  let timedOut = false
  const abortFromCaller = () => controller?.abort()
  if (externalSignal && controller) {
    if (externalSignal.aborted) controller.abort()
    else externalSignal.addEventListener('abort', abortFromCaller, { once: true })
  }
  const timer = controller && timeoutMs > 0
    ? setTimeout(() => {
        timedOut = true
        controller.abort()
      }, timeoutMs)
    : null
  const requestSignal = controller?.signal || externalSignal
  try {
    const headers = getHeaders(path, { json: body !== undefined })
    const res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      credentials: 'include',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(requestSignal ? { signal: requestSignal } : {}),
    })
    if (!res.ok) await throwForStatus(res, path, headers['X-Participant-Token'] || null)
    // Await the body here so the timer and caller cancellation remain active
    // after headers arrive. A stalled JSON download otherwise never recovers.
    return await res.json()
  } catch (err) {
    if (err?.name === 'AbortError') {
      if (!timedOut && externalSignal?.aborted) {
        const abortedError = new Error('Anfrage wurde durch einen neueren Datenabruf ersetzt.')
        abortedError.code = 'REQUEST_ABORTED'
        abortedError.method = method
        throw abortedError
      }
      const timeoutError = new Error(
        method === 'GET'
          ? 'Verbindung unterbrochen. Sie wird wiederhergestellt.'
          : 'Keine Antwort erhalten. Bitte prüfen Sie, ob die Aktion ausgeführt wurde, bevor Sie es erneut versuchen.'
      )
      timeoutError.code = 'REQUEST_TIMEOUT'
      timeoutError.method = method
      throw timeoutError
    }
    throw err
  } finally {
    if (timer) clearTimeout(timer)
    externalSignal?.removeEventListener?.('abort', abortFromCaller)
  }
}

export async function apiGet (path, { timeoutMs = DEFAULT_API_TIMEOUT_MS, signal } = {}) {
  return conferenceRequest(path, { timeoutMs, signal })
}

export async function apiPost (path, body = {}, { timeoutMs = DEFAULT_API_TIMEOUT_MS } = {}) {
  return conferenceRequest(path, { method: 'POST', body, timeoutMs })
}

export async function apiPut (path, body = {}, { timeoutMs = DEFAULT_API_TIMEOUT_MS } = {}) {
  return conferenceRequest(path, { method: 'PUT', body, timeoutMs })
}

export async function apiDelete (path, body, { timeoutMs = DEFAULT_API_TIMEOUT_MS } = {}) {
  return conferenceRequest(path, { method: 'DELETE', body, timeoutMs })
}

async function authRequest (
  path,
  {
    method = 'GET',
    body,
    timeoutMs = DEFAULT_AUTH_TIMEOUT_MS,
  } = {}
) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = controller && timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null
  try {
    const res = await fetch(`${AUTH_URL}${path}`, {
      method,
      credentials: 'include',
      headers: body === undefined
        ? { Accept: 'application/json' }
        : { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(controller ? { signal: controller.signal } : {}),
    })
    const data = await res.json().catch(error => {
      if (error?.name === 'AbortError') throw error
      return {}
    })
    return { res, data }
  } catch (err) {
    if (err?.name === 'AbortError') {
      const timeoutError = new Error('Authentifizierungsanfrage hat zu lange gedauert. Bitte versuchen Sie es erneut.')
      timeoutError.code = 'REQUEST_TIMEOUT'
      timeoutError.method = method
      throw timeoutError
    }
    throw err
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/** Login → server sets an HttpOnly session cookie → returns admin info. */
export async function authLogin(username, password, mfaCode = '', { timeoutMs = DEFAULT_AUTH_TIMEOUT_MS } = {}) {
  const { res, data } = await authRequest('/login', {
    method: 'POST',
    body: { username, password, mfa_code: mfaCode || undefined },
    timeoutMs,
  })
  if (!res.ok) {
    const err = new Error(data.error || 'Login failed')
    err.status = res.status
    err.mfaRequired = !!data.mfa_required
    throw err
  }
  // The JWT is delivered as an HttpOnly cookie by the server — nothing to store
  // client-side. Subsequent requests send it automatically via credentials.
  return data.admin
}

/**
 * Verify the session cookie. A real 401/403 means logged out; transport and
 * server failures are thrown so callers can reconnect without destroying the
 * valid local session state.
 */
export async function authMe({ timeoutMs = 10000 } = {}) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = controller && timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null
  try {
    const res = await fetch(`${AUTH_URL}/me`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(controller ? { signal: controller.signal } : {}),
    })
    if (res.status === 401 || res.status === 403) return null
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      const err = new Error(data.error || `HTTP ${res.status}`)
      err.status = res.status
      throw err
    }
    const data = await res.json()
    return data.admin || null
  } catch (err) {
    if (err?.name === 'AbortError') throw new Error('Authentication check timed out')
    throw err
  } finally {
    if (timer) clearTimeout(timer)
  }
}

async function authMutation(path, body = {}, { timeoutMs = DEFAULT_AUTH_TIMEOUT_MS } = {}) {
  const { res, data } = await authRequest(path, {
    method: 'POST',
    body,
    timeoutMs,
  })
  if (!res.ok) {
    const err = new Error(data.error || 'Sicherheitsaktion fehlgeschlagen')
    err.status = res.status
    throw err
  }
  return data
}

export const authMfaSetup = (options) => authMutation('/mfa/setup', {}, options)
export const authMfaConfirm = (mfaCode, options) => authMutation('/mfa/confirm', { mfa_code: mfaCode }, options)
export const authMfaDisable = (mfaCode, options) => authMutation('/mfa/disable', { mfa_code: mfaCode }, options)

/** Log out: server clears the HttpOnly cookie; also drop any legacy JS cookie. */
export async function authLogout({ timeoutMs = DEFAULT_AUTH_TIMEOUT_MS } = {}) {
  const { res, data } = await authRequest('/logout', {
    method: 'POST',
    body: {},
    timeoutMs,
  })
  if (!res.ok) {
    const err = new Error(data.error || 'Abmelden fehlgeschlagen')
    err.status = res.status
    throw err
  }
  // Only clear JS-readable legacy cookies after the server confirms that the
  // HttpOnly session cookie was invalidated. A transport failure keeps the
  // dashboard and session intact so the operator can retry safely.
  clearLegacyAuthCookies()
}

export { BASE_URL, SOCKET_URL, CONF_TOKEN_KEY }
